import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useModal } from '../hooks/useModal';

import Spinner from '../components/common/Spinner/Spinner';
import EmptyState from '../components/common/EmptyState/EmptyState';
import BackButton from '../components/common/BackButton/BackButton';
import RespostaCardPublic from '../components/RespostaCardPublic';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import QuestaoCard from '../components/common/QuestaoCard/QuestaoCard';
import { calculateIAP, getClassificacaoIAP } from '../utils/iap';
import { calcularMediaGeral } from '../utils/diagnosticoStats';
import { exportToPdf } from '../utils/exportUtils';
import './QuestoesOrg.css';
import './DetalheQuestaoModal.css';
import { EIXOS } from '../constants/eixos';
import { SearchableSelectBase } from '../components/common/SearchableSelect/SearchableSelect';
import { FaChevronLeft, FaChevronRight, FaSearch, FaFilePdf } from 'react-icons/fa';

// Função para formatar a pontuação de forma segura
const formatarPontuacao = (p) => {
  const pont = parseFloat(p);
  return isNaN(pont) ? 0 : Math.round(pont * 100);
};

// Novo componente do Modal, mais rico e bem estruturado

const getFaseClasse = (p) => {
  if (p >= 75) return 'aprimorado';
  if (p >= 50) return 'intermediario';
  if (p >= 15) return 'inicial';
  return 'inexpressivo';
};

const MATURIDADE_OPCOES = [
  { id: 'aprimorado', label: 'Aprimorado' },
  { id: 'intermediario', label: 'Intermediário' },
  { id: 'inicial', label: 'Inicial' },
  { id: 'inexpressivo', label: 'Inexpressivo' },
];

const QuestoesPublicas = () => {
  const { orgId } = useParams();
  const { showModal, hideModal } = useModal();
  const [loading, setLoading] = useState(true);
  const [questoes, setQuestoes] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedEixo, setSelectedEixo] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedMaturidade, setSelectedMaturidade] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [allQuestoes, setAllQuestoes] = useState([]); // Store all fetched questions
  const [priorizacoes, setPriorizacoes] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24; // Gráfico 3x8 para melhor aproveitamento do grid

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const functions = getFunctions(getApp(), 'southamerica-east1');
        const getDiagnosticoDetalhado = httpsCallable(functions, 'getDiagnosticoDetalhadoPublico');
        const result = await getDiagnosticoDetalhado({ orgId });
        const fetchedQuestoes = result.data.questoes;
        setOrgData(result.data.orgData);
        setAllQuestoes(fetchedQuestoes); // Store all questions

        // Carregar priorizações
        const priorizacoesSnap = await getDocs(collection(db, 'organizacoes', orgId, 'priorizacoes'));
        const mapaPriorizacoes = {};
        priorizacoesSnap.forEach(doc => {
          mapaPriorizacoes[doc.id] = doc.data();
        });
        setPriorizacoes(mapaPriorizacoes);

      } catch (e) {
        console.error("Erro ao chamar a Cloud Function:", e);
        setError(e.message || "Ocorreu um erro ao buscar os dados.");
        setQuestoes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  useEffect(() => {
    let filtered = allQuestoes;

    if (selectedEixo !== 'all') {
      filtered = filtered.filter(q => q.eixo === selectedEixo);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(q => {
        const isAnswered = q.resposta?.resposta_principal && q.resposta.resposta_principal !== 'Não informado';
        return selectedStatus === 'respondida' ? isAnswered : !isAnswered;
      });
    }

    if (selectedMaturidade.length > 0) {
      filtered = filtered.filter(q => {
        const percent = formatarPontuacao(q.resposta?.pontuacao);
        const maturidade = getFaseClasse(percent);
        return selectedMaturidade.includes(maturidade);
      });
    }

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(q =>
        q.codigo.toLowerCase().includes(lowerCaseSearchTerm) ||
        q.enunciado.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setQuestoes(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allQuestoes, selectedEixo, selectedStatus, selectedMaturidade, searchTerm]);

  const currentItems = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return questoes.slice(startIndex, startIndex + itemsPerPage);
  }, [questoes, currentPage]);

  const totalPages = Math.ceil(questoes.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCardClick = (questao) => {
    showModal({
      body: <RespostaCardPublic questao={questao} />,
      onConfirm: hideModal,
      modalProps: {
        className: 'custom-modal',
        closeOnOverlayClick: true,
      }
    });
  };

  const handleExportAll = () => {
    const colunas = [
      { key: 'codigo', label: 'Código' },
      { key: 'enunciado', label: 'Enunciado' },
      { key: 'eixo', label: 'Tema' },
      { key: 'pontuacao', label: 'Pontuação' },
      { key: 'nivel_maturidade', label: 'Nível de Maturidade' },
      { key: 'resposta_principal', label: 'Resposta Principal' },
      { key: 'alternativas_marcadas', label: 'Itens de Verificação' },
      { key: 'itens_detalhados', label: 'Itens (Texto)' },
    ];

    const preparo = allQuestoes.map(q => {
      const resp = q.resposta || {};
      const totalAdicionais = Array.isArray(q.questoes_adicionais) ? q.questoes_adicionais.length : 0;
      const marcadas = Array.isArray(resp.questoes_adicionais) ? resp.questoes_adicionais.length : 0;
      const pont = (resp.pontuacao || 0) * 100;

      let maturidade = 'Inexpressivo';
      if (pont >= 70) maturidade = 'Aprimorado';
      else if (pont >= 40) maturidade = 'Intermediário';
      else if (pont >= 15) maturidade = 'Inicial';

      return {
        codigo: q.codigo,
        enunciado: q.enunciado,
        eixo: q.eixo,
        pontuacao: `${pont.toFixed(1)}%`,
        nivel_maturidade: maturidade,
        alternativas_marcadas: `${marcadas}/${totalAdicionais}`,
        itens_detalhados: Array.isArray(q.questoes_adicionais)
          ? q.questoes_adicionais.map(item => {
              const isChecked = Array.isArray(resp.questoes_adicionais) && resp.questoes_adicionais.includes(item);
              return `${isChecked ? '[x]' : '[ ]'} ${item}`;
            }).join('\n')
          : '',
        resposta_principal: resp.resposta_principal || 'Pendente'
      };
    });

    preparo.sort((a, b) => (a.codigo || '').localeCompare(b.codigo || '', undefined, { numeric: true }));

    const respondidasCount = allQuestoes.filter(q => q.resposta?.resposta_principal).length;
    const mediaGeral = calcularMediaGeral(allQuestoes);

    const dashboardRow = {
      dashboard_mediaGeral: `${mediaGeral.toFixed(1)}%`,
      dashboard_taxaParticipacao: `${((respondidasCount / allQuestoes.length) * 100).toFixed(1)}%`,
    };

    exportToPdf(colunas, [...preparo, dashboardRow], orgData?.nome || 'Organização');
  };

  return (
    <div className="view-container">
      <div className="questoes-publicas-content">
        <BackButton label="Voltar ao Diagnóstico" />
        <header className="view-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <h1>{`Diagnóstico Detalhado: ${orgData?.nome || 'Organização'}`}</h1>
          <button className="action-btn outline" onClick={handleExportAll} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
            <FaFilePdf /> Exportar PDF Completo
          </button>
        </header>
        <div className="filtros-container" style={{ alignItems: 'flex-end' }}>
          <div className="filtro-item" style={{ minWidth: '220px' }}>
            <label htmlFor="eixo-filter">Tema:</label>
            <SearchableSelectBase
              options={[{ value: 'all', label: 'Todos os Temas' }, ...EIXOS]}
              value={selectedEixo}
              onChange={setSelectedEixo}
              placeholder="Todos os Temas"
            />
          </div>

          <div className="filtro-item" style={{ minWidth: '180px' }}>
            <label htmlFor="status-filter">Status:</label>
            <SearchableSelectBase
              options={[
                { id: 'all', label: 'Todos os Status' },
                { id: 'respondida', label: 'Respondida' },
                { id: 'pendente', label: 'Pendente' }
              ]}
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Todos os Status"
            />
          </div>

          <div className="filtro-item" style={{ minWidth: '250px' }}>
            <label>Maturidade:</label>
            <SearchableSelectBase
              multiple
              options={MATURIDADE_OPCOES}
              value={selectedMaturidade}
              onChange={setSelectedMaturidade}
              placeholder="Todos os Níveis"
            />
          </div>

          <div className="filtro-item search-bar-custom" style={{ minWidth: '300px' }}>
            <label htmlFor="search-input" className="sr-only">Pesquisar:</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <FaSearch style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                zIndex: 5,
                fontSize: '0.9rem'
              }} />
              <input
                type="text"
                id="search-input"
                className="modern-input"
                placeholder="Pesquisar por código ou enunciado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="questoes-cards-container">
          {loading ? (
            <Spinner text="Carregando questões..." fullScreen />

          ) : error ? (
            <EmptyState message={`Erro: ${error}`} />
          ) : questoes.length > 0 ? (
            currentItems.map(q => {
              const p = priorizacoes[q.id];
              const iap = p ? calculateIAP(p.missao, p.seguranca, p.viabilidade) : null;
              const classificacao = getClassificacaoIAP(iap);

              const formattedQuestao = {
                ...q,
                id: q.id,
                pontuacao: q.resposta?.pontuacao || 0,
                status: q.resposta?.resposta_principal && q.resposta.resposta_principal !== 'Não informado' ? 'RESPONDIDA' : 'PENDENTE',
                priorizacao: classificacao
              };

              return (
                <div key={q.id} className="questao-card-wrapper">
                  <QuestaoCard
                    questao={formattedQuestao}
                    onClick={() => handleCardClick(formattedQuestao)}
                  />
                </div>
              );
            })
          ) : (
            <EmptyState message="Nenhuma questão encontrada." />
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginTop: '2rem',
            marginBottom: '3rem'
          }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="icon-button"
              style={{
                background: '#f1f5f9',
                padding: '8px 12px',
                borderRadius: '8px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <FaChevronLeft />
            </button>
            <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600' }}>
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="icon-button"
              style={{
                background: '#f1f5f9',
                padding: '8px 12px',
                borderRadius: '8px',
                opacity: currentPage === totalPages ? 0.5 : 1,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestoesPublicas;
